// SCR-STAFF-606 — صدور گواهی و نامه (US-DOC-052؛ AC-DOC-052.1، 052.2؛ C-AGENT-1488، 1489): متن از قالب نمونه (نشان «نمونه» — متن نهایی با پزشک)،
// نوع (انجام خدمت، استراحت با بازه، عمومی)، خدمت اداری پولی اختیاری، امضا با PIN؛ فهرست گواهی‌ها با شماره، وضعیت، PDF، ارسال به پرتال و ابطال با دلیل.
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { unwrap, type DocCertificateKind, type DocCertificateView } from '@phoenix/api-client';
import { useAccess, useIdempotencyKey, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { DataTable, ErrorState, FormDialog, ReasonDialog, SampleBadge, StatusChip, toJalali, type DataTableColumn } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { PinDialog } from '../clinical/common';
import { saveDocBlob } from './common';

export const SCR_STAFF_606 = 'SCR-STAFF-606';

const KINDS: readonly NonNullable<DocCertificateKind>[] = ['ServiceAttendance', 'Rest', 'General'];

function fill(body: string, values: Record<string, string>): string {
  return Object.entries(values).reduce((text, [key, value]) => text.replaceAll(`{${key}}`, value), body);
}

export function PatientCertificatesPanel({ patientId }: { patientId: string }) {
  const { t } = useTranslation('doc');
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const queryClient = useQueryClient();
  const key = useIdempotencyKey();
  const tenantId = session?.tenantId;
  const canIssue = access.accessMode === 'Full' && access.can('doc.certificate.issue');
  const [composing, setComposing] = useState(false);
  const [kind, setKind] = useState<NonNullable<DocCertificateKind>>('ServiceAttendance');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [restFrom, setRestFrom] = useState('');
  const [restTo, setRestTo] = useState('');
  const [serviceId, setServiceId] = useState('');
  const [signing, setSigning] = useState(false);
  const [revoking, setRevoking] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const listKey = ['documents', tenantId, 'certificates', patientId] as const;
  const list = useQuery({ queryKey: listKey, queryFn: () => unwrap(tenantApi.GET('/api/v1/documents/patients/{patientId}/certificates', { params: { path: { patientId } } })) });
  const templates = useQuery({
    queryKey: ['documents', tenantId, 'certificate-templates'],
    enabled: canIssue,
    queryFn: () => unwrap(tenantApi.GET('/api/v1/documents/certificates/templates')),
    staleTime: 300_000,
  });
  const services = useQuery({
    queryKey: ['documents', tenantId, 'administrative-services'],
    enabled: composing,
    retry: false,
    queryFn: () => unwrap(tenantApi.GET('/api/v1/catalog/services', { params: { query: { status: 'Active', limit: 200 } } })),
  });
  const template = templates.data?.items.find((item) => item.kind === kind);
  const startCompose = () => {
    setComposing(true);
    applyTemplate('ServiceAttendance');
  };
  const applyTemplate = (next: NonNullable<DocCertificateKind>) => {
    setKind(next);
    const found = templates.data?.items.find((item) => item.kind === next);
    setTitle(found?.title ?? '');
    setBody(found?.body ?? '');
  };
  const issue = useMutation({
    mutationFn: (pin: string) =>
      unwrap(
        tenantApi.POST('/api/v1/documents/certificates', {
          body: {
            patientId,
            encounterId: null,
            kind,
            title: title.trim() || null,
            body: fill(body, { from: restFrom ? toJalali(restFrom) : '', to: restTo ? toJalali(restTo) : '', date: toJalali(new Date().toISOString().slice(0, 10)) }),
            restFrom: kind === 'Rest' ? restFrom || null : null,
            restTo: kind === 'Rest' ? restTo || null : null,
            serviceId: serviceId || null,
            pin,
          },
          headers: { 'Idempotency-Key': key.current() },
        }),
      ),
    onSettled: (_data, error) => key.settle(error ?? undefined),
    onSuccess: (created) => {
      setSigning(false);
      setComposing(false);
      setNotice(t('doc:certificate.issued', { number: created.number }));
      void queryClient.invalidateQueries({ queryKey: listKey });
    },
  });
  const revoke = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => unwrap(tenantApi.POST('/api/v1/documents/certificates/{id}:revoke', { params: { path: { id } }, body: { reason } })),
    onSuccess: () => {
      setRevoking(null);
      void queryClient.invalidateQueries({ queryKey: listKey });
    },
  });
  const share = useMutation({
    mutationFn: (id: string) => unwrap(tenantApi.POST('/api/v1/documents/certificates/{id}:share', { params: { path: { id } }, body: { patientId: null, channel: 'Portal' }, headers: { 'Idempotency-Key': globalThis.crypto.randomUUID() } })),
    onSuccess: () => setNotice(t('doc:certificate.shared')),
  });
  const pdf = async (row: DocCertificateView) => {
    const response = await tenantApi.GET('/api/v1/documents/certificates/{id}/pdf', { params: { path: { id: row.id } }, parseAs: 'blob' });
    if (response.response.ok && response.data) {
      saveDocBlob(response.data as unknown as Blob, `${row.number}.pdf`);
    }
  };
  const columns: DataTableColumn<DocCertificateView>[] = [
    { id: 'number', headerKey: 'doc:certificate.number', cell: (row) => <bdi dir="ltr">{row.number}</bdi> },
    { id: 'kind', headerKey: 'doc:certificate.kind', cell: (row) => `${t(`doc:certificate.kinds.${row.kind}`)}${row.restDays ? ` · ${t('doc:certificate.rest_days', { count: Number(row.restDays) })}` : ''}` },
    { id: 'date', headerKey: 'doc:certificate.issued_on', cell: (row) => toJalali(row.issuedOn) },
    { id: 'provider', headerKey: 'doc:certificate.provider', cell: (row) => row.providerName ?? '—' },
    { id: 'status', headerKey: 'doc:certificate.status', cell: (row) => <StatusChip tone={row.status === 'Issued' ? 'done' : 'error'} labelKey={`doc:certificate.statuses.${row.status}`} /> },
    {
      id: 'actions',
      headerKey: 'doc:certificate.actions',
      cell: (row) => (
        <Stack direction="row" spacing={0.5}>
          {access.can('doc.document.download') && (
            <Button size="small" onClick={() => void pdf(row)} data-testid="doc-certificate-pdf">
              {t('doc:certificate.pdf')}
            </Button>
          )}
          {row.status === 'Issued' && access.can('doc.document.share') && (
            <Button size="small" onClick={() => share.mutate(row.id)} data-testid="doc-certificate-share">
              {t('doc:certificate.share_portal')}
            </Button>
          )}
          {row.status === 'Issued' && canIssue && (
            <Button size="small" color="error" onClick={() => setRevoking(row.id)} data-testid="doc-certificate-revoke">
              {t('doc:certificate.revoke')}
            </Button>
          )}
        </Stack>
      ),
    },
  ];
  const restValid = kind !== 'Rest' || (restFrom !== '' && restTo !== '' && restTo >= restFrom);
  return (
    <Stack spacing={1.5} data-screen-id={SCR_STAFF_606} data-testid="doc-certificates">
      <Stack direction="row" sx={{ alignItems: 'center' }}>
        <Typography variant="subtitle1" component="h3" sx={{ flexGrow: 1 }}>
          {t('doc:certificate.title')}
        </Typography>
        {canIssue && (
          <Button variant="contained" onClick={startCompose} data-testid="doc-certificate-new">
            {t('doc:certificate.new')}
          </Button>
        )}
      </Stack>
      {notice && (
        <Alert severity="success" onClose={() => setNotice(null)} data-testid="doc-certificate-notice">
          {notice}
        </Alert>
      )}
      {share.error ? <ErrorState error={share.error} compact /> : null}
      <DataTable captionKey="doc:certificate.title" columns={columns} rows={list.data?.items} getRowId={(row) => row.id} isLoading={list.isPending} error={list.error} onRetry={() => void list.refetch()} emptyTitleKey="doc:certificate.empty" />
      {composing && (
        <FormDialog
          open
          titleKey="doc:certificate.new"
          submitKey="doc:certificate.sign_and_issue"
          onSubmit={() => (body.trim() && restValid ? setSigning(true) : undefined)}
          onClose={() => setComposing(false)}
          isDirty={body.trim() !== ''}
          isSubmitting={issue.isPending}
          submitError={undefined}
        >
          <TextField select label={t('doc:certificate.kind')} value={kind} onChange={(event) => applyTemplate(event.target.value as NonNullable<DocCertificateKind>)} slotProps={{ htmlInput: { 'data-testid': 'doc-certificate-kind' } }}>
            {KINDS.map((value) => (
              <MenuItem key={value} value={value}>
                {t(`doc:certificate.kinds.${value}`)}
              </MenuItem>
            ))}
          </TextField>
          {template?.isSample && (
            <Alert severity="warning" icon={false} data-testid="doc-certificate-sample">
              <SampleBadge /> {t('doc:certificate.sample_text')}
            </Alert>
          )}
          <TextField label={t('doc:certificate.title_field')} value={title} onChange={(event) => setTitle(event.target.value)} slotProps={{ htmlInput: { maxLength: 200 } }} />
          {kind === 'Rest' && (
            <Stack direction="row" spacing={1}>
              <TextField type="date" required label={t('doc:certificate.rest_from')} value={restFrom} onChange={(event) => setRestFrom(event.target.value)} slotProps={{ inputLabel: { shrink: true }, htmlInput: { 'data-testid': 'doc-certificate-rest-from' } }} />
              <TextField type="date" required label={t('doc:certificate.rest_to')} value={restTo} onChange={(event) => setRestTo(event.target.value)} slotProps={{ inputLabel: { shrink: true }, htmlInput: { 'data-testid': 'doc-certificate-rest-to' } }} />
            </Stack>
          )}
          <TextField required label={t('doc:certificate.body')} helperText={t('doc:certificate.body_hint')} value={body} multiline minRows={4} onChange={(event) => setBody(event.target.value)} slotProps={{ htmlInput: { maxLength: 4000, 'data-testid': 'doc-certificate-body' } }} />
          <TextField select label={t('doc:certificate.service')} helperText={t('doc:certificate.service_hint')} value={serviceId} onChange={(event) => setServiceId(event.target.value)} slotProps={{ htmlInput: { 'data-testid': 'doc-certificate-service' } }}>
            <MenuItem value="">{t('doc:certificate.free')}</MenuItem>
            {(services.data?.items ?? []).filter((item) => item.type === 'Administrative').map((item) => (
              <MenuItem key={item.id} value={item.id}>
                {item.name}
              </MenuItem>
            ))}
          </TextField>
        </FormDialog>
      )}
      <PinDialog
        open={signing}
        titleKey="doc:certificate.sign_and_issue"
        onClose={() => {
          issue.reset();
          setSigning(false);
        }}
        onConfirm={(pin) => issue.mutate(pin)}
        isSubmitting={issue.isPending}
        error={issue.error}
      >
        <Typography variant="body2">{t('doc:certificate.sign_message')}</Typography>
      </PinDialog>
      <ReasonDialog open={revoking !== null} titleKey="doc:certificate.revoke" messageKey="doc:certificate.revoke_message" busy={revoke.isPending} error={revoke.error} onConfirm={(reason) => revoking && revoke.mutate({ id: revoking, reason })} onCancel={() => setRevoking(null)} />
    </Stack>
  );
}
